SCOPE: ONE COHERENT USER JOURNEY

  A workflow should test one job-to-be-done, not multiple unrelated tasks.
  Many steps are fine if they're all part of the same flow (creating,
  editing, verifying one entity). Stop when the goal is accomplished.

  Good — coherent journey:
    Login → Navigate to projects → Create project → Edit name →
    Assert name updated → Cleanup: delete project

  Good — multi-role journey:
    Login as Customer → Place an order →
    Login as Sales Rep → Verify the order appears in the queue

  Good — read-only flow:
    Login → Navigate to dashboard → Assert widgets visible

  Bad — multiple unrelated journeys:
    Dashboard → Create item → Manage users → Generate report
    (These are separate jobs — create separate workflows)

  Rule: "Is this step dependent on or naturally connected to the previous
  steps?" If no, you're starting a new journey — stop.

  A coherent flow can be long or short — there is no target node count. If a
  flow grows very long, ask whether it is really several INDEPENDENT journeys:
  splitting them into separate tests lets them run in parallel and fail
  independently. Keep a single coherent journey together — don't split mid-flow.

WRITE STEPS AS HUMAN INTENT

  A workflow should read like instructions you'd explain to a teammate:
    - "Create a ticket assigned to Support."
    - "Submit the ticket and confirm it appears in the list."
    - "Change the ticket status to Closed."

  Capture the "why" and the "what" — not UI mechanics.

CHOOSE THE RIGHT GRANULARITY

  Too granular:
    - "Click New"
    - "Type title"
    - "Type description"
    - "Click Save"

  Too stuffed:
    - "Create a ticket, assign it, comment, upload, and verify in three views"

  Good:
    - "Create a ticket with required fields"
    - "Assign the ticket to Support"
    - "Verify the ticket appears in the queue"

  Sizing rule: a node is a single coherent goal of roughly 3–8 playbook steps
  (one meaningful action each — a whole form-fill is ONE fill_form step). The
  "too granular" list above is about splitting one goal into a node per keystroke
  or writing mechanics-only titles — NOT a ban on single-action nodes: a lone
  action that stands on its own ("Click Start to open the modal") is fine. This
  ~3–8 is per action node (its playbook STEPS); it is NOT a cap on how many NODES
  a flow has — flow length follows the journey (see SCOPE above). See the Action
  Nodes section.

  If an action crosses multiple screens or changes multiple entities, split it.
  If it's a single user goal on a single screen, keep it as one action.

MAKE EACH TEST SELF-SUFFICIENT

  Tests should not depend on what another test created or on long-lived
  shared state. If a workflow needs data, provision it:

  - Use a seed workflow (preferred) to generate data and export variables
  - Use a setup node to reference a published workflow for shared data
  - As a last resort, add data creation steps at the start of the workflow
  - Always use macros ($EMAIL, $UUID, etc.) so reruns don't collide

  This keeps runs deterministic and prevents cascading failures.

VERIFY EVERY SIGNIFICANT ACTION

  Before recording a step, verify it worked:

  - Form submissions: confirm acceptance (success message, redirect, new item in list)
  - Data creation: navigate to where the record should appear and confirm
  - Data modification: reload and confirm the change persisted
  - Deletions: confirm the item is gone (a toast alone is not enough)
  - Navigation: confirm the expected page loaded

  If verification fails, debug the issue — don't record and move on.

KEEP DESCRIPTIONS CLEAR

  Node titles should be short and action-oriented:
    Good: "Create a support ticket"
    Bad:  "Step 3 - ticket creation process"

  Node descriptions should add detail the title can't carry:
    Good: "Fill the form with a QA Test subject and High priority, then submit"
    Bad:  "Do the thing"

NODE ORDERING

  The start-of-workflow order is a strict hierarchy (see "STARTING NODE"
  in the Node Types guide):

    Seed (if needed) → Login → Navigate → ...

  The first node is the ACTOR, not a navigate: an authenticated flow opens
  with a `login` node (or a `seed`, if it needs data first) — never with a
  navigate node. A navigate jumps to the test's entry point AFTER login.
  Only unauthenticated flows (signup, password reset, public pages, or a
  login test) may start with a navigate.

  Seed nodes should come first. Seeds run in their own isolated context
  (usually via API sequence) and export variables the rest of the flow
  consumes. Putting a login before the seed wastes a browser segment,
  and any login/navigate that references $var.{seed_output} must come
  after the seed that produced it.

  Only skip the seed-first rule if the seed itself genuinely depends on
  a browser session created earlier in the same workflow (rare — in
  that case, prefer a separate browser-based seed workflow and reference
  it here).

LOGIN BEST PRACTICES

  - Most workflows start with a login node. Workflows can also start
    with a seed node or API sequence when no browser session is needed yet.
  - Login SUBSUMES logging in. A login node already provisions an
    authenticated session — it IS the act of logging in. Never follow a
    login node with "navigate to /login" / "enter email + password" /
    "click Sign in" steps. Start with the login node and go straight to
    the first real action. (Only exception: a flow whose purpose is to
    test login — start logged out and make the login steps the journey.)
  - Login nodes require a credentialId (UUID), not just a credentialName.
    Without a credentialId the Flow Designer flags the node as invalid.
    Find credential IDs with:   canary credential list [--env prod|dev]
  - Use multiple login nodes for multi-role testing — each login starts
    a new segment with a fresh session as that user
  - Don't test login itself (unless that's the explicit goal)
  - A navigate node after login is a great way to jump directly to the
    test's entry point

PREFER INLINE ASSERTIONS

  Attach assertions to action nodes using inlineAssertions rather than
  adding standalone assertion nodes. Inline assertions keep the test
  focused and produce clearer failure reports. See the Assertions section
  for details.